import {useCallback, useMemo, useState} from 'react';

import type {ShoppingItem as UdemyShoppingItem} from '@udemy/shopping';

interface CandidateState {
    isLoading: boolean;
    shoppingItems?: UdemyShoppingItem[];
}

export interface SelectedUpsell {
    kind: 'course' | 'voucher';
    id: number;
    shoppingItems: UdemyShoppingItem[];
}

export interface UseUpsellSelectionParams {
    cartCourseItems: UdemyShoppingItem[];
    cartVoucherItems: UdemyShoppingItem[];
}

export interface UseUpsellSelectionResult {
    /** The single upsell row chosen by the cascade, or `null` when none qualify. */
    selected: SelectedUpsell | null;
    /** `true` while the active path still has unsettled candidates. Hide the section while pending. */
    isPending: boolean;
    isSelected: (kind: SelectedUpsell['kind'], id: number) => boolean;
    /** Records the candidate's loading flag — fed by the row's `onLoadingChange`. */
    setLoading: (kind: SelectedUpsell['kind'], id: number, isLoading: boolean) => void;
    /** Records the candidate's resolved content — fed by the row's `onHasContent`. */
    setContent: (
        kind: SelectedUpsell['kind'],
        id: number,
        hasContent: boolean,
        shoppingItems?: UdemyShoppingItem[],
    ) => void;
}

type Selection = {selected: SelectedUpsell; isPending: false} | {selected: null; isPending: true};

function pickUpsell(
    kind: SelectedUpsell['kind'],
    cartItems: UdemyShoppingItem[],
    candidates: Record<number, CandidateState>,
): Selection | null {
    let firstUpsellWithContent: SelectedUpsell | null = null;
    for (const item of cartItems) {
        const candidate = candidates[item.buyable.id];
        if (!candidate || candidate.isLoading) return {selected: null, isPending: true};
        if (!firstUpsellWithContent && candidate.shoppingItems?.length) {
            firstUpsellWithContent = {
                kind,
                id: item.buyable.id,
                shoppingItems: candidate.shoppingItems,
            };
        }
    }
    return firstUpsellWithContent ? {selected: firstUpsellWithContent, isPending: false} : null;
}

/**
 * Picks at most one upsell to render: first cart course with renderable
 * content, else first cart voucher with renderable content, else nothing.
 * The course path must fully settle before the voucher path is considered, so
 * the section doesn't flicker. Tie-break is cart order — sort the inputs
 * upstream when the All-Vouchers ranking lands.
 */
export function useUpsellSelection({
    cartCourseItems,
    cartVoucherItems,
}: UseUpsellSelectionParams): UseUpsellSelectionResult {
    const [courseCandidates, setCourseCandidates] = useState<Record<number, CandidateState>>({});
    const [voucherCandidates, setVoucherCandidates] = useState<Record<number, CandidateState>>({});

    const setLoading = useCallback(
        (kind: SelectedUpsell['kind'], id: number, isLoading: boolean) => {
            const setCandidates = kind === 'course' ? setCourseCandidates : setVoucherCandidates;
            setCandidates((prev) => {
                const existing = prev[id];
                if (existing?.isLoading === isLoading) return prev;
                return {...prev, [id]: {isLoading, shoppingItems: existing?.shoppingItems}};
            });
        },
        [],
    );

    const setContent = useCallback(
        (
            kind: SelectedUpsell['kind'],
            id: number,
            hasContent: boolean,
            shoppingItems?: UdemyShoppingItem[],
        ) => {
            const nextItems = hasContent && shoppingItems?.length ? shoppingItems : undefined;
            const setCandidates = kind === 'course' ? setCourseCandidates : setVoucherCandidates;
            setCandidates((prev) => {
                const existing = prev[id];
                if (existing && existing.shoppingItems === nextItems) return prev;
                return {
                    ...prev,
                    [id]: {isLoading: existing?.isLoading ?? true, shoppingItems: nextItems},
                };
            });
        },
        [],
    );

    const {selected, isPending} = useMemo<{
        selected: SelectedUpsell | null;
        isPending: boolean;
    }>(
        () =>
            pickUpsell('course', cartCourseItems, courseCandidates) ??
            pickUpsell('voucher', cartVoucherItems, voucherCandidates) ?? {
                selected: null,
                isPending: false,
            },
        [cartCourseItems, cartVoucherItems, courseCandidates, voucherCandidates],
    );

    const isSelected = useCallback(
        (kind: SelectedUpsell['kind'], id: number) => selected?.kind === kind && selected.id === id,
        [selected],
    );

    return {
        selected,
        isPending,
        isSelected,
        setLoading,
        setContent,
    };
}
